import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Container,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import Iconify from "components/iconify";
import customFetch from "hooks/fetchWrapper";

export default function FilterSection() {
  const { setValue, watch } = useFormContext();
  const selectedEmirate = watch("emirate");
  const selectedArea = watch("area");
  const routeSearch = watch("routeSearch") || "";

  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loadingEmirates, setLoadingEmirates] = useState(true);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [allRoutes, setAllRoutes] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Area search states
  const [areaSearch, setAreaSearch] = useState("");
  const [showAreaDropdown, setShowAreaDropdown] = useState(false);

  const searchRef = useRef(null);
  const areaRef = useRef(null);

  // Load Emirates
  useEffect(() => {
    const loadEmirates = async () => {
      try {
        setLoadingEmirates(true);
        const data = await customFetch("emirates");
        setEmirates(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error("Failed to load emirates:", err);
        setEmirates([]);
      } finally {
        setLoadingEmirates(false);
      }
    };
    loadEmirates();
  }, []);

  // Load Areas when Emirate selected
  useEffect(() => {
    if (!selectedEmirate) {
      setAreas([]);
      setValue("area", "");
      setAreaSearch("");
      return;
    }
    const loadAreas = async () => {
      try {
        setLoadingAreas(true);
        const res = await customFetch(`areas/emirate/${selectedEmirate}`);
        setAreas(res.status && res.data ? res.data : []);
      } catch (err) {
        console.error("Failed to load areas:", err);
        setAreas([]);
      } finally {
        setLoadingAreas(false);
      }
    };
    loadAreas();
  }, [selectedEmirate, setValue]);

  // Load all routes+stops when area changes (to power the bustop autocomplete)
  useEffect(() => {
    setAllRoutes([]);
    setValue("selectedBusStop", null);
    if (!selectedArea) return;

    const loadRoutes = async () => {
      try {
        const res = await customFetch(`routes/by-area-with-stops/${selectedArea}`);
        setAllRoutes(res.status && Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to load routes for autocomplete:", err);
        setAllRoutes([]);
      }
    };
    loadRoutes();
  }, [selectedArea, setValue]);

  // Filtered areas based on search input
  const filteredAreas = useMemo(() => {
    const searchLower = areaSearch?.toLowerCase().trim();

    const result = searchLower
      ? areas.filter(
          (a) =>
            a.areaname?.toLowerCase().includes(searchLower) ||
            a.code?.toLowerCase().includes(searchLower)
        )
      : [...areas];

    // Always sort alphabetically by area name
    return result.sort((a, b) => (a.areaname || "").localeCompare(b.areaname || ""));
  }, [areas, areaSearch]);

  // Build flat list of matching bus stops from all routes
  const suggestedStops = useMemo(() => {
    if (!routeSearch || routeSearch.length < 1) return [];
    const searchLower = routeSearch.toLowerCase();
    const results = [];
    allRoutes.forEach((route) => {
      (route.bustops || []).forEach((stop) => {
        if (stop.bustopname?.toLowerCase().includes(searchLower)) {
          results.push({ stop, route });
        }
      });
    });
    return results;
  }, [routeSearch, allRoutes]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (areaRef.current && !areaRef.current.contains(e.target)) {
        setShowAreaDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleStopSelect = (stop, route) => {
    setValue("routeSearch", stop.bustopname);
    setValue("selectedBusStop", { stop, route });
    setShowDropdown(false);
  };

  const inputSx = {
    maxWidth: 350,
    "& .MuiOutlinedInput-root": {
      borderRadius: 50,
      bgcolor: "white",
      fontSize: "1.1rem",
      "& fieldset": { borderWidth: 2, borderColor: "#4caf50" },
      "&:hover fieldset": { borderColor: "#388e3c" },
      "&.Mui-focused fieldset": { borderColor: "#4caf50", borderWidth: 2 },
    },
    "& .MuiInputLabel-root": {
      fontSize: "1.1rem",
      fontWeight: 500,
      "&.Mui-focused": { color: "#4caf50" },
    },
  };

  const scrollablePaperSx = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    zIndex: 1300,
    borderRadius: 3,
    border: "1.5px solid #4caf50",
    maxHeight: 300,
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { bgcolor: "#f1f1f1", borderRadius: 3 },
    "&::-webkit-scrollbar-thumb": {
      bgcolor: "#4caf50",
      borderRadius: 3,
      "&:hover": { bgcolor: "#388e3c" },
    },
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
      >
        {/* Emirate Dropdown */}
        <FormControl fullWidth sx={inputSx}>
          <InputLabel id="emirate-label">Emirate</InputLabel>
          <Select
            labelId="emirate-label"
            value={selectedEmirate}
            label="Emirate"
            onChange={(e) => {
              setValue("emirate", e.target.value);
              setValue("area", "");
              setValue("routeSearch", "");
              setValue("selectedBusStop", null);
              setAreaSearch("");
              setShowAreaDropdown(false);
            }}
            disabled={loadingEmirates}
            sx={{ py: 1 }}
          >
            <MenuItem value="" disabled>
              Select Emirate
            </MenuItem>
            {emirates.map((e) => (
              <MenuItem key={e.eid} value={e.eid}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Area Searchable Dropdown */}
        <Box sx={{ position: "relative", maxWidth: 350, width: "100%" }} ref={areaRef}>
          <TextField
            fullWidth
            label="Area"
            placeholder={!selectedEmirate ? "Select emirate first..." : "Search or select area..."}
            value={areaSearch}
            onChange={(e) => {
              setAreaSearch(e.target.value);
              setShowAreaDropdown(true);
              if (selectedArea) {
                setValue("area", "");
                setValue("routeSearch", "");
                setValue("selectedBusStop", null);
              }
            }}
            onFocus={() => {
              if (selectedEmirate) setShowAreaDropdown(true);
            }}
            disabled={!selectedEmirate || loadingAreas}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {loadingAreas ? (
                    <CircularProgress size={20} sx={{ color: "#4caf50" }} />
                  ) : (
                    <>
                      {/* Clear Button - Cross Icon */}
                      {areaSearch && (
                        <Iconify
                          icon="mdi:close-circle"
                          sx={{
                            color: "#757575",
                            fontSize: 22,
                            cursor: "pointer",
                            mr: 0.5,
                            "&:hover": { color: "#f44336" },
                          }}
                          onClick={() => {
                            // Clear everything
                            setAreaSearch("");
                            setValue("area", "");
                            setValue("routeSearch", "");
                            setValue("selectedBusStop", null);
                            setShowAreaDropdown(false);
                          }}
                        />
                      )}

                      {/* Chevron Icon */}
                      <Iconify
                        icon={showAreaDropdown ? "mdi:chevron-up" : "mdi:chevron-down"}
                        sx={{ color: "#4caf50", fontSize: 24, cursor: "pointer" }}
                        onClick={() => {
                          if (selectedEmirate) setShowAreaDropdown((prev) => !prev);
                        }}
                      />
                    </>
                  )}
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          {/* Area Dropdown List */}
          {showAreaDropdown && filteredAreas.length > 0 && (
            <Paper elevation={6} sx={scrollablePaperSx}>
              <List dense disablePadding>
                {filteredAreas.map((a, idx) => {
                  const label = a.code ? `${a.areaname}` : a.areaname;
                  const isSelected = selectedArea === a.aid;
                  return (
                    <ListItemButton
                      key={a.aid}
                      selected={isSelected}
                      onClick={() => {
                        setValue("area", a.aid);
                        setValue("routeSearch", "");
                        setValue("selectedBusStop", null);
                        setAreaSearch(label);
                        setShowAreaDropdown(false);
                      }}
                      divider={idx < filteredAreas.length - 1}
                      sx={{
                        py: 1.2,
                        px: 2,
                        bgcolor: isSelected ? "#e8f5e9 !important" : "inherit",
                        "&:hover": { bgcolor: "#e8f5e9" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={isSelected ? 700 : 500}
                            color="text.primary"
                          >
                            {label}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {a.emirateName}
                          </Typography>
                        }
                      />
                      {isSelected && (
                        <Iconify icon="mdi:check" sx={{ color: "#4caf50", fontSize: 20, ml: 1 }} />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Paper>
          )}

          {/* No match message */}
          {showAreaDropdown && areaSearch && filteredAreas.length === 0 && (
            <Paper elevation={6} sx={{ ...scrollablePaperSx, maxHeight: "auto" }}>
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No areas found matching "{areaSearch}"
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Bustop Search Input with Autocomplete Dropdown */}
        <Box ref={searchRef} sx={{ position: "relative", maxWidth: 350, width: "100%" }}>
          <TextField
            fullWidth
            label="Search Bustop"
            placeholder="Search by bustop name..."
            value={routeSearch}
            onChange={(e) => {
              setValue("routeSearch", e.target.value);
              setValue("selectedBusStop", null);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            disabled={!selectedArea}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="mdi:magnify" sx={{ color: "#4caf50", fontSize: 24 }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          {/* Bustop Suggestions Dropdown */}
          {showDropdown && suggestedStops.length > 0 && (
            <Paper elevation={6} sx={scrollablePaperSx}>
              <List dense disablePadding>
                {suggestedStops.map(({ stop, route }, idx) => (
                  <ListItemButton
                    key={`${stop.sid}-${route.rid}`}
                    onClick={() => handleStopSelect(stop, route)}
                    divider={idx < suggestedStops.length - 1}
                    sx={{
                      py: 1.2,
                      px: 2,
                      "&:hover": { bgcolor: "#e8f5e9" },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="600" color="text.primary">
                          {stop.bustopname}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Route {route.routeno}
                          {/* · {stop.pickupcode} · {stop.pickuptime} */}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Stack>

      {(loadingEmirates || loadingAreas) && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress sx={{ color: "#4caf50" }} />
        </Box>
      )}
    </Container>
  );
}
